<template>
    <div class="flex flex-col gap-4">
        <PropertyDetailCard
            v-if="bookingDetail && Object.keys(bookingDetail).length"
            :bookingDetail="bookingDetail"
            :isShowLocation="isShowLocation"
        />

        <div class="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-white p-4">
            <p class="text-sm font-semibold text-gray-800">{{ $t("checkout.addon.title") }}</p>
            <p class="mt-1 text-xs text-gray-600">
                {{ $t("checkout.addon.description") }}
            </p>
            <div v-if="isSyncing" class="mt-2 flex items-center gap-2 text-xs font-medium text-blue-700">
                <AppIcon icon="mdi:loading" class="animate-spin" width="14" height="14" />
                {{ $t("checkout.addon.saving") }}
            </div>
        </div>

        <div v-if="normalizedAddons.length === 0" class="rounded-xl border border-dashed p-4 text-sm text-gray-600">
            {{ $t("checkout.addon.empty") }}
        </div>

        <div
            v-for="addon in normalizedAddons"
            :key="addon.RoomAddOnId"
            class="flex flex-col gap-3 rounded-xl border p-3 shadow-sm transition-all"
            :class="{
                'bg-gray-50 opacity-70': isUnavailable(addon),
                'border-blue-200 bg-blue-50/40': isSelectedAddon(addon.RoomAddOnId),
            }"
            @click="handleAddonCardClick(addon, $event)"
        >
            <div class="addon-row flex items-start justify-between gap-3">
                <div class="addon-info flex cursor-pointer items-start gap-3">
                    <input
                        type="checkbox"
                        class="mt-1 h-5 w-5 accent-blue-600"
                        :checked="isSelectedAddon(addon.RoomAddOnId)"
                        :disabled="isSyncing || isUnavailable(addon) || isRequired(addon)"
                        @click.stop
                        @change.stop="toggleAddon(addon)"
                    />
                    <div>
                        <div class="flex flex-wrap items-center gap-2">
                            <span class="text-sm font-semibold">{{ addon.Name }}</span>
                            <span class="text-sm font-semibold text-blue-600">
                                {{ $formatters.formatCurrency(addon.Price) }}
                            </span>
                            <span v-if="isRequired(addon)" class="rule-badge rule-badge-selection">{{ $t("checkout.addon.required") }}</span>
                        </div>
                        <p v-if="addon.Description" class="text-xs text-gray-500">
                            {{ addon.Description }}
                        </p>
                        <p v-if="getPricingHint(addon)" class="mt-1 text-[11px] font-medium text-gray-500">
                            {{ getPricingHint(addon) }}
                        </p>
                        <p v-if="isUnavailable(addon)" class="mt-1 text-xs font-medium text-red-600">
                            {{ $t("checkout.addon.inactive") }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="isSelectedAddon(addon.RoomAddOnId)"
                    class="addon-side flex flex-col items-end gap-2"
                    data-addon-control="true"
                >
                    <div v-if="canAdjustQuantity(addon)" class="qty-total-row" data-addon-control="true">
                        <div class="addon-total-price">
                            {{ $formatters.formatCurrency(calculateAddOnTotal(addon)) }}
                        </div>
                        <div class="qty-row" data-addon-control="true">
                            <div class="qty-shell" data-addon-control="true">
                                <button
                                    class="qty-action"
                                    data-addon-control="true"
                                    :disabled="isSyncing || selectedMap[addon.RoomAddOnId].Quantity <= getMinQuantity(addon)"
                                    @click="decreaseQuantity(addon)"
                                >
                                    <AppIcon icon="mdi:minus" width="14" height="14" />
                                </button>
                                <div data-addon-control="true">
                                    <input
                                        v-model.number="selectedMap[addon.RoomAddOnId].Quantity"
                                        type="number"
                                        class="qty-value-input no-spinner"
                                        data-addon-control="true"
                                        :min="getMinQuantity(addon)"
                                        :max="getMaxQuantity(addon)"
                                        :disabled="isSyncing || isUnavailable(addon)"
                                        @input="validateQuantity(addon)"
                                    />
                                </div>
                                <button
                                    class="qty-action"
                                    data-addon-control="true"
                                    :disabled="isSyncing || selectedMap[addon.RoomAddOnId].Quantity >= getMaxQuantity(addon)"
                                    @click="increaseQuantity(addon)"
                                >
                                    <AppIcon icon="mdi:plus" width="14" height="14" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else class="addon-total-price">
                        {{ $formatters.formatCurrency(calculateAddOnTotal(addon)) }}
                    </div>
                </div>
            </div>
        </div>

        <div class="rounded-xl border bg-blue-50 p-3 text-sm">
            <div class="flex items-center justify-between font-semibold">
                <span>{{ $t("checkout.addon.subtotal") }}</span>
                <span>{{ $formatters.formatCurrency(addonsSubtotal) }}</span>
            </div>
        </div>

        <p v-if="hasInvalidRequiredSelection" class="text-sm font-medium text-amber-700">
            {{ $t("checkout.addon.requiredMissing") }}
        </p>
        <p v-if="errorMessage" class="text-sm font-medium text-red-600">{{ errorMessage }}</p>
    </div>
</template>

<script>
const DEFAULT_SELECTION_MODE = "OPTIONAL";
const DEFAULT_INPUT_MODE = "QUANTITY";
const DEFAULT_PRICING_MODE = "FIXED_PER_BOOKING";

export default {
    name: "AddonModal",
    props: {
        addons: { type: Array, required: true, default: () => [] },
        bookingDetail: { type: Object, required: true, default: () => ({}) },
        isShowLocation: { type: Boolean, required: false, default: false },
    },
    data() {
        return {
            selectedMap: {},
            errorMessage: "",
            isSyncing: false,
            syncTimer: null,
            isHydrating: true,
            lastSyncedSignature: "",
        };
    },
    computed: {
        cartDetailId() {
            return this.bookingDetail?.Id || null;
        },
        nights() {
            return Number(this.bookingDetail?.TotalNights || 1) || 1;
        },
        pax() {
            return Number(this.bookingDetail?.Pax || this.bookingDetail?.Room?.Pax || 1) || 1;
        },
        normalizedAddons() {
            return (this.addons || [])
                .map((addon) => ({
                    ...addon,
                    SelectionMode: addon.SelectionMode || DEFAULT_SELECTION_MODE,
                    InputMode: this.normalizeInputMode(addon.InputMode),
                    PricingMode: addon.PricingMode || DEFAULT_PRICING_MODE,
                    MinQuantity: Number(addon.MinQuantity ?? 1),
                    MaxQuantity: Number(addon.MaxQuantity ?? 1),
                    DefaultQuantity: Number(addon.DefaultQuantity ?? addon.MinQuantity ?? 1),
                    IsActive: addon.IsActive !== false,
                    SortOrder: Number(addon.SortOrder ?? 0),
                }))
                .filter((addon) => addon.IsActive !== false)
                .sort((a, b) => {
                    if (a.SortOrder !== b.SortOrder) return a.SortOrder - b.SortOrder;
                    return String(a.Name || "").localeCompare(String(b.Name || ""));
                });
        },
        selectedAddonsPayload() {
            return Object.values(this.selectedMap).map((entry) => ({
                RoomAddOnId: entry.RoomAddOnId,
                Quantity: Number(entry.Quantity || 1),
            }));
        },
        addonsSubtotal() {
            return this.normalizedAddons.reduce((total, addon) => {
                if (!this.isSelectedAddon(addon.RoomAddOnId)) return total;
                return total + this.calculateAddOnTotal(addon);
            }, 0);
        },
        hasInvalidRequiredSelection() {
            return this.normalizedAddons.some((addon) => this.isRequired(addon) && !this.isSelectedAddon(addon.RoomAddOnId));
        },
    },
    methods: {
        getPricingHint(addon) {
            const map = {
                FIXED_PER_BOOKING: "",
                FIXED_PER_NIGHT: this.$t("checkout.addon.perNight"),
                FIXED_PER_PERSON: this.$t("checkout.addon.perPerson"),
                FIXED_PER_PERSON_PER_NIGHT:
                    this.$t("checkout.addon.perPersonPerNight"),
            };
            return map[addon?.PricingMode] || "";
        },
        getMinQuantity(addon) {
            const min = Number(addon.MinQuantity || 1);
            return min > 0 ? min : 1;
        },
        getMaxQuantity(addon) {
            const max = Number(addon.MaxQuantity || 1);
            return max >= this.getMinQuantity(addon) ? max : this.getMinQuantity(addon);
        },
        canAdjustQuantity(addon) {
            if (!this.isQuantityMode(addon)) return false;
            return this.getMaxQuantity(addon) > this.getMinQuantity(addon);
        },
        isRequired(addon) {
            return addon.SelectionMode === "REQUIRED";
        },
        normalizeInputMode(value) {
            const normalized = String(value || "").trim().toUpperCase();
            if (
                normalized === "BOOLEAN" ||
                normalized === "TOGGLE" ||
                normalized === "SINGLE_SELECT" ||
                normalized === "SINGLE"
            ) {
                return "BOOLEAN";
            }
            if (normalized === "QUANTITY" || normalized === "QTY") {
                return "QUANTITY";
            }
            return DEFAULT_INPUT_MODE;
        },
        isQuantityMode(addon) {
            return this.normalizeInputMode(addon?.InputMode) === "QUANTITY";
        },
        isUnavailable(addon) {
            return addon.IsActive === false;
        },
        isSelectedAddon(roomAddOnId) {
            return Boolean(this.selectedMap[roomAddOnId]);
        },
        handleAddonCardClick(addon, event) {
            if (this.isSyncing) return;
            const target = event?.target;
            if (target?.closest("[data-addon-control='true']")) return;
            if (target?.closest("input[type='checkbox']")) return;
            this.toggleAddon(addon);
        },
        toggleAddon(addon) {
            if (this.isUnavailable(addon)) return;
            if (this.isRequired(addon)) return;

            if (this.isSelectedAddon(addon.RoomAddOnId)) {
                delete this.selectedMap[addon.RoomAddOnId];
                this.selectedMap = { ...this.selectedMap };
                this.queueRealtimeSync();
                return;
            }

            const quantity = this.isQuantityMode(addon)
                ? Math.min(Math.max(addon.DefaultQuantity, this.getMinQuantity(addon)), this.getMaxQuantity(addon))
                : 1;

            this.selectedMap = {
                ...this.selectedMap,
                [addon.RoomAddOnId]: {
                    RoomAddOnId: addon.RoomAddOnId,
                    Quantity: quantity,
                },
            };
            this.queueRealtimeSync();
        },
        validateQuantity(addon) {
            const current = this.selectedMap[addon.RoomAddOnId];
            if (!current) return;

            if (!this.isQuantityMode(addon)) {
                current.Quantity = 1;
                return;
            }

            let value = Number(current.Quantity || 1);
            if (!Number.isFinite(value)) value = this.getMinQuantity(addon);
            if (value < this.getMinQuantity(addon)) value = this.getMinQuantity(addon);
            if (value > this.getMaxQuantity(addon)) value = this.getMaxQuantity(addon);
            current.Quantity = value;
            this.queueRealtimeSync();
        },
        increaseQuantity(addon) {
            if (!this.canAdjustQuantity(addon)) return;
            const current = this.selectedMap[addon.RoomAddOnId];
            if (!current) return;
            current.Quantity = Number(current.Quantity || 1) + 1;
            this.validateQuantity(addon);
        },
        decreaseQuantity(addon) {
            if (!this.canAdjustQuantity(addon)) return;
            const current = this.selectedMap[addon.RoomAddOnId];
            if (!current) return;
            current.Quantity = Number(current.Quantity || 1) - 1;
            this.validateQuantity(addon);
        },
        calculateAddOnTotal(addon) {
            const selected = this.selectedMap[addon.RoomAddOnId];
            if (!selected) return 0;

            const qty = this.isQuantityMode(addon) ? Number(selected.Quantity || 1) : 1;
            const unit = Number(addon.Price || 0);

            switch (addon.PricingMode) {
                case "FIXED_PER_NIGHT":
                    return unit * qty * this.nights;
                case "FIXED_PER_PERSON":
                    return unit * qty * this.pax;
                case "FIXED_PER_PERSON_PER_NIGHT":
                    return unit * qty * this.pax * this.nights;
                case "FIXED_PER_BOOKING":
                default:
                    return unit * qty;
            }
        },
        getErrorMessage(error) {
            const code = error?.response?.data?.code;
            const fallback = error?.response?.data?.msg || this.$t("checkout.addon.saveFailed");
            const map = {
                ADDON_NOT_VALID_FOR_CART_DETAIL: this.$t("checkout.addon.notValidForRoom"),
                ADDON_DUPLICATE_IN_REQUEST: this.$t("checkout.addon.duplicate"),
                ADDON_QUANTITY_INVALID: this.$t("checkout.addon.quantityInvalid"),
                ADDON_REQUIRED_MISSING: this.$t("checkout.addon.requiredNotFulfilled"),
            };
            return map[code] || fallback;
        },
        initializeSelectedMap() {
            const next = {};
            const existing = this.bookingDetail?.BookDetailAddOns || [];
            const activeAddOnIdSet = new Set(
                this.normalizedAddons.map((addon) => Number(addon.RoomAddOnId))
            );
            const hasExistingSelection = existing.length > 0;

            if (existing.length > 0) {
                existing.forEach((selected) => {
                    if (!activeAddOnIdSet.has(Number(selected.RoomAddOnId))) return;
                    next[selected.RoomAddOnId] = {
                        RoomAddOnId: selected.RoomAddOnId,
                        Quantity: Number(selected.Quantity || 1),
                    };
                });
            } else {
                this.normalizedAddons.forEach((addon) => {
                    if (this.isUnavailable(addon)) return;
                    if (addon.SelectionMode === "DEFAULT_SELECTED" || this.isRequired(addon)) {
                        next[addon.RoomAddOnId] = {
                            RoomAddOnId: addon.RoomAddOnId,
                            Quantity: this.isQuantityMode(addon)
                                ? Math.min(Math.max(addon.DefaultQuantity, this.getMinQuantity(addon)), this.getMaxQuantity(addon))
                                : 1,
                        };
                    }
                });
            }

            this.selectedMap = next;
            this.normalizedAddons.forEach((addon) => this.validateQuantity(addon));
            this.isHydrating = false;
            // Jika sudah ada snapshot selection di cart detail, anggap sinkron.
            // Jika belum ada snapshot (mis. default-selected/required dari rule), paksa sync awal
            // agar total checkout langsung ter-update sebelum user lanjut ke metode pembayaran.
            this.lastSyncedSignature = hasExistingSelection ? this.createSyncSignature() : JSON.stringify([]);
            if (!hasExistingSelection && this.selectedAddonsPayload.length > 0) {
                this.queueRealtimeSync();
            }
        },
        createSyncSignature() {
            const normalized = this.selectedAddonsPayload
                .map((item) => ({
                    RoomAddOnId: Number(item.RoomAddOnId),
                    Quantity: Number(item.Quantity || 1),
                }))
                .sort((a, b) => a.RoomAddOnId - b.RoomAddOnId);
            return JSON.stringify(normalized);
        },
        queueRealtimeSync() {
            if (this.isHydrating) return;
            if (!this.cartDetailId) return;
            if (this.hasInvalidRequiredSelection) return;

            if (this.syncTimer) {
                clearTimeout(this.syncTimer);
            }
            this.syncTimer = setTimeout(() => {
                this.syncTimer = null;
                this.syncAddonsRealtime();
            }, 250);
        },
        async syncAddonsRealtime() {
            if (!this.cartDetailId || this.isHydrating || this.hasInvalidRequiredSelection) return;
            if (this.isSyncing) return;

            const nextSignature = this.createSyncSignature();
            if (nextSignature === this.lastSyncedSignature) return;

            this.errorMessage = "";
            this.isSyncing = true;

            try {
                await this.$store.dispatch("cart/updateCartDetail", {
                    CartDetailId: this.cartDetailId,
                    RoomAddOns: this.selectedAddonsPayload,
                });
                this.lastSyncedSignature = nextSignature;
                this.$emit("updated");
            } catch (error) {
                this.errorMessage = this.getErrorMessage(error);
                console.error("Error updating cart detail:", error);
            } finally {
                this.isSyncing = false;
                if (this.createSyncSignature() !== this.lastSyncedSignature) {
                    this.queueRealtimeSync();
                }
            }
        },
    },
    beforeUnmount() {
        if (this.syncTimer) {
            clearTimeout(this.syncTimer);
            this.syncTimer = null;
            this.syncAddonsRealtime();
        }
    },
    mounted() {
        this.initializeSelectedMap();
    },
};
</script>

<style scoped>
.no-spinner::-webkit-inner-spin-button,
.no-spinner::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.rule-badge {
    border-radius: 9999px;
    border: 1px solid #d1d5db;
    background: #f9fafb;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 600;
    color: #374151;
}

.rule-badge-selection {
    border-color: #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
}

.addon-info {
    flex: 1 1 auto;
    min-width: 0;
}

.qty-total-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.addon-total-price {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1;
}

.qty-row {
    display: flex;
    align-items: center;
}

.qty-shell {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    border-radius: 9999px;
    padding: 1px;
    width: fit-content;
}

.qty-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid #bfdbfe;
    border-radius: 9999px;
    background: #fff;
    font-weight: 700;
    color: #1d4ed8;
    transition: all 0.2s ease;
}

.qty-action:hover:not(:disabled) {
    background: #dbeafe;
}

.qty-action:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.qty-value-wrap {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 8px;
}

.qty-value-input {
    max-height: 12px;
    border: none;
    background: transparent;
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: #111827;
    outline: none;
}

@media (max-width: 640px) {
    .addon-row {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .addon-side {
        align-items: stretch;
        width: 100%;
    }

    .qty-total-row {
        justify-content: space-between;
        width: 100%;
    }

    .addon-total-price {
        font-size: 15px;
    }
}
</style>
